import { useTranslation } from '@govard/i18n-core/react';
import { useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Switch } from 'antd';

import type { MemoryRecordDto } from '../api/memory.client.js';
import { useForgetMemoryMutation, useUpdateMemoryMutation } from '../hooks/useMemoryMutations.js';
import { useMemorySearch } from '../hooks/useMemorySearch.js';
import { useRecentMemories } from '../hooks/useRecentMemories.js';
import { ApiError } from '../../../shared/api-client/index.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { FilterBar, FilterSearch } from '../../../shared/ui/FilterBar.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';

type MemoryTab = 'l1' | 'l2' | 'l3';

interface MemoryFormValues {
  valueText: string;
  importance: number;
  pinned: boolean;
}

/**
 * 501 means the semantic search is not wired in this environment (no embeddings
 * provider). That is permanent, so the panel names it instead of offering a
 * retry that cannot succeed.
 */
function isSearchUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 501;
}

export function MemoryBrowserPage(): ReactElement {
  const { t } = useTranslation();
  // The level and the search phrase live in the URL, so a level or a query can
  // be linked to (and survives a reload) instead of resetting to L1.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: MemoryTab = requestedTab === 'l2' || requestedTab === 'l3' ? requestedTab : 'l1';
  const query = searchParams.get('q') ?? '';
  // The phrase that was actually searched. The input value (`query`) follows the
  // URL on every keystroke; the request only runs on submit, and the deep link
  // seeds this from `?q=` so a shared link searches once on open.
  const [submittedQuery, setSubmittedQuery] = useState(() =>
    activeTab === 'l3' ? query.trim() : '',
  );

  const setParam = (name: 'tab' | 'q', value: string): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === '') next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: true },
    );
  };

  // Запись заводит агент с подтверждением пользователя — здесь можно только
  // исправить формулировку и забыть ошибочный факт.
  const [editing, setEditing] = useState<MemoryRecordDto | null>(null);
  const updateMut = useUpdateMemoryMutation();
  const forgetMut = useForgetMemoryMutation();
  const [form] = Form.useForm<MemoryFormValues>();

  const l1Query = useRecentMemories('l1', 30);
  const l2Query = useRecentMemories('l2', 30);
  const l3Query = useRecentMemories('l3', 30);
  // A query, not a mutation: see the hook — a mutation lost its observer when
  // StrictMode remounted the page and the panel stayed in `isPending` forever.
  const searchQuery = useMemorySearch(submittedQuery);

  const activeItems = useMemo(() => {
    if (activeTab === 'l1') {
      return l1Query.data?.items ?? [];
    }
    if (activeTab === 'l2') {
      return l2Query.data?.items ?? [];
    }
    return l3Query.data?.items ?? [];
  }, [activeTab, l1Query.data?.items, l2Query.data?.items, l3Query.data?.items]);

  const activeQuery = activeTab === 'l1' ? l1Query : activeTab === 'l2' ? l2Query : l3Query;

  const searchItems = searchQuery.data?.items ?? [];
  const searchUnavailable = isSearchUnavailable(searchQuery.error);

  function openEditor(item: MemoryRecordDto): void {
    setEditing(item);
    form.setFieldsValue({
      valueText: item.valueText,
      importance: item.importance,
      pinned: item.pinned ?? false,
    });
  }

  async function handleSave(values: MemoryFormValues): Promise<void> {
    if (!editing?.updatedAt) return;
    await updateMut.mutateAsync({
      id: editing.id,
      input: {
        valueText: values.valueText,
        importance: values.importance,
        pinned: values.pinned,
        expectedUpdatedAt: editing.updatedAt,
      },
    });
    setEditing(null);
  }

  async function handleForget(item: MemoryRecordDto): Promise<void> {
    if (!item.updatedAt) return;
    // Забывание инвалидирует корень `memory` (см. useForgetMemoryMutation), а
    // выдача поиска лежит под тем же корнем — запись уходит из неё вместе со
    // списком уровня, без локальной правки состояния.
    await forgetMut.mutateAsync({ id: item.id, expectedUpdatedAt: item.updatedAt });
  }

  function handleSearch(): void {
    const normalized = query.trim();
    if (normalized === '') return;
    // The same phrase is already the query key, so setting the state again would
    // change nothing; a re-click has to force the request instead.
    if (normalized === submittedQuery) {
      void searchQuery.refetch();
      return;
    }
    setSubmittedQuery(normalized);
  }

  return (
    <main
      className="animate-fade-in flex flex-col p-6 mx-auto w-full"
      style={{
        backgroundColor: 'var(--ant-color-bg-layout)',
        minHeight: '100vh',
        maxWidth: 1200,
        gap: 20,
      }}
    >
      <PageHeader title={t('crm.memory.title')} subtitle={t('crm.memory.subtitle')} />

      <div
        className="flex flex-col"
        style={{
          padding: 24,
          background: 'var(--ant-color-bg-container)',
          border: '1px solid var(--ant-color-border-secondary)',
          borderRadius: 'var(--ant-border-radius-lg)',
        }}
      >
        <div className="mb-5">
          {/* Spec D .panel-title: micro-label над описанием. */}
          <h2
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: 'var(--ant-color-text-secondary)',
              margin: 0,
            }}
          >
            {t('crm.memory.browser_title')}
          </h2>
          <p style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', marginTop: 4 }}>
            {t('crm.memory.browser_description')}
          </p>
        </div>

        <div
          className="flex flex-wrap gap-2 mb-6"
          role="tablist"
          aria-label={t('crm.memory.tabs_aria')}
        >
          {(['l1', 'l2', 'l3'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setParam('tab', tab)}
              role="tab"
              aria-selected={activeTab === tab}
              type="button"
              className="transition-colors"
              style={{
                height: 28,
                padding: '0 12px',
                fontSize: 12,
                fontWeight: activeTab === tab ? 550 : 500,
                borderRadius: 10,
                backgroundColor: activeTab === tab ? 'var(--ant-color-primary-bg)' : 'transparent',
                color:
                  activeTab === tab
                    ? 'var(--ant-color-primary-text)'
                    : 'var(--ant-color-text-secondary)',
                border:
                  activeTab === tab
                    ? '1px solid var(--ant-color-primary-border)'
                    : '1px solid var(--ant-color-border)',
                cursor: 'pointer',
              }}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>

        {activeTab === 'l3' ? (
          <section
            className="flex flex-col gap-4 mb-6"
            style={{
              padding: 16,
              backgroundColor: 'var(--ant-color-bg-elevated)',
              border: '1px solid var(--ant-color-border-secondary)',
              borderRadius: 10,
            }}
          >
            {/* The raw input + button were the only ones on the page; the list
                chrome uses the shared FilterBar everywhere else. */}
            <FilterBar
              search={
                <FilterSearch
                  id="memory-search-input"
                  aria-label={t('crm.memory.search_label')}
                  placeholder={t('crm.memory.search_placeholder')}
                  value={query}
                  onChange={(value) => setParam('q', value)}
                  onPressEnter={handleSearch}
                />
              }
            >
              <Button
                type="primary"
                size="small"
                data-testid="memory-search-btn"
                loading={searchQuery.isFetching}
                onClick={handleSearch}
              >
                {t('crm.memory.search_submit')}
              </Button>
            </FilterBar>

            {searchQuery.isFetching ? (
              <div
                className="flex items-center gap-2"
                style={{ fontSize: 13, color: 'var(--ant-color-text-tertiary)' }}
              >
                <span>{t('crm.memory.search_loading')}</span>
              </div>
            ) : searchQuery.isError ? (
              // A failed search must not read as "nothing matched": the empty
              // state is reserved for a query that actually ran.
              <ErrorState
                title={
                  searchUnavailable
                    ? t('crm.memory.search_unavailable')
                    : t('crm.memory.search_error')
                }
                {...(searchUnavailable
                  ? { fallbackDescription: t('crm.memory.search_unavailable_hint') }
                  : { error: searchQuery.error, onRetry: () => void searchQuery.refetch() })}
              />
            ) : null}

            {searchQuery.isError ? null : searchItems.length > 0 ? (
              <div className="flex flex-col gap-2" data-testid="memory-search-results">
                {searchItems.map((item) => (
                  <article
                    key={item.id}
                    className="transition-colors"
                    style={{
                      padding: '12px 16px',
                      backgroundColor: 'var(--ant-color-bg-container)',
                      border: '1px solid var(--ant-color-border-secondary)',
                      borderRadius: 10,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className="font-medium"
                        style={{ fontSize: 14, color: 'var(--ant-color-text)' }}
                      >
                        {item.key}
                      </p>
                      <span
                        className="rounded-full flex-shrink-0"
                        style={{
                          fontSize: 10,
                          padding: '2px 8px',
                          fontVariantNumeric: 'tabular-nums',
                          backgroundColor: 'var(--ant-color-bg-elevated)',
                          border: '1px solid var(--ant-color-border-secondary)',
                          color: 'var(--ant-color-text-secondary)',
                        }}
                      >
                        {t('crm.memory.score', { score: item.score.toFixed(2) })}
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: 13,
                        color: 'var(--ant-color-text-tertiary)',
                        marginTop: 4,
                      }}
                    >
                      {item.valueText}
                    </p>
                    <MemoryActions
                      item={item}
                      onEdit={openEditor}
                      onForget={handleForget}
                      forgetting={
                        forgetMut.isPending &&
                        typeof forgetMut.variables !== 'string' &&
                        forgetMut.variables?.id === item.id
                      }
                      t={t}
                    />
                  </article>
                ))}
              </div>
            ) : null}

            {!searchQuery.isError &&
            !searchQuery.isFetching &&
            submittedQuery !== '' &&
            searchItems.length === 0 ? (
              <QuietEmpty text={t('crm.memory.search_empty', { query: submittedQuery })} />
            ) : null}
          </section>
        ) : null}

        <section className="flex flex-col gap-3" data-testid={`memory-tab-${activeTab}`}>
          {activeQuery.isLoading ? (
            <QuietListSkeleton rows={3} />
          ) : activeQuery.isError ? (
            <ErrorState
              title={t('crm.memory.load_error')}
              error={activeQuery.error}
              onRetry={() => void activeQuery.refetch()}
            />
          ) : activeItems.length === 0 ? (
            <QuietEmpty text={t('crm.memory.empty')} />
          ) : null}
          {activeItems.map((item) => (
            <article
              key={item.id}
              className="transition-colors"
              style={{
                border: '1px solid var(--ant-color-border-secondary)',
                borderRadius: 10,
                padding: '12px 16px',
                backgroundColor: 'var(--ant-color-bg-container)',
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium" style={{ fontSize: 14, color: 'var(--ant-color-text)' }}>
                  {item.key}
                </p>
                <span
                  className="rounded-full flex-shrink-0"
                  style={{
                    fontSize: 10,
                    padding: '2px 8px',
                    fontVariantNumeric: 'tabular-nums',
                    backgroundColor: 'var(--ant-color-bg-elevated)',
                    color: 'var(--ant-color-text-secondary)',
                  }}
                >
                  {t('crm.memory.importance', { value: item.importance })}
                </span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--ant-color-text-tertiary)', marginTop: 4 }}>
                {item.valueText}
              </p>
              <MemoryActions
                item={item}
                onEdit={openEditor}
                onForget={handleForget}
                forgetting={
                  forgetMut.isPending &&
                  typeof forgetMut.variables !== 'string' &&
                  forgetMut.variables?.id === item.id
                }
                t={t}
              />
            </article>
          ))}
        </section>
      </div>

      <Modal
        open={editing !== null}
        title={editing === null ? '' : t('crm.memory.edit_title', { key: editing.key })}
        okText={t('crm.memory.actions.save')}
        cancelText={t('crm.memory.actions.cancel')}
        confirmLoading={updateMut.isPending}
        onOk={() => form.submit()}
        onCancel={() => setEditing(null)}
        destroyOnHidden
      >
        {updateMut.error ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            title={t('crm.memory.save_error')}
            description={updateMut.error.message}
          />
        ) : null}
        <Form<MemoryFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => void handleSave(values)}
        >
          <Form.Item
            name="valueText"
            label={t('crm.memory.field.value')}
            rules={[{ required: true, message: t('crm.memory.field.value_required') }]}
            extra={editing?.level === 'l3' ? t('crm.memory.field.value_reembed_hint') : undefined}
          >
            <Input.TextArea rows={4} maxLength={5000} />
          </Form.Item>
          <Form.Item name="importance" label={t('crm.memory.field.importance')}>
            <InputNumber min={1} max={5} />
          </Form.Item>
          <Form.Item name="pinned" label={t('crm.memory.field.pinned')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </main>
  );
}

interface MemoryActionsProps {
  item: MemoryRecordDto;
  onEdit: (item: MemoryRecordDto) => void;
  onForget: (item: MemoryRecordDto) => Promise<void>;
  forgetting: boolean;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Действия над записью — одинаковые в списке уровня и в выдаче поиска. */
function MemoryActions({
  item,
  onEdit,
  onForget,
  forgetting,
  t,
}: MemoryActionsProps): ReactElement {
  return (
    <div
      className="flex items-center gap-2 mt-3 pt-3"
      style={{ borderTop: '1px solid var(--ant-color-border-secondary)' }}
    >
      <button
        type="button"
        style={{
          fontSize: 12,
          padding: '4px 10px',
          borderRadius: 6,
          border: '1px solid var(--ant-color-border)',
          background: 'transparent',
          color: 'var(--ant-color-text-secondary)',
          cursor: 'pointer',
        }}
        onClick={() => onEdit(item)}
      >
        {t('crm.memory.actions.edit')}
      </button>
      <Popconfirm
        title={t('crm.memory.forget_confirm')}
        description={t('crm.memory.forget_description')}
        okText={t('crm.memory.actions.forget')}
        cancelText={t('crm.memory.actions.cancel')}
        okButtonProps={{ danger: true, loading: forgetting }}
        onConfirm={() => onForget(item)}
      >
        <button
          type="button"
          style={{
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 6,
            border: '1px solid var(--ant-color-border)',
            background: 'transparent',
            color: 'var(--ant-color-error-text)',
            cursor: 'pointer',
          }}
        >
          {t('crm.memory.actions.forget')}
        </button>
      </Popconfirm>
    </div>
  );
}
